import type { AudienceGroup } from '../audience';

export type FileKind = 'sheet' | 'audio' | 'lyrics';
export type SheetTier = 's' | 'm' | 'l';
export const SHEET_TIER_WIDTH: Record<SheetTier, number> = { s: 800, m: 1600, l: 2752 };
export type MediaPart = 'orig' | SheetTier;

export interface PageSize {
  /** Page width/height in PDF points (rotation applied). Only the aspect ratio matters. */
  w: number;
  h: number;
}

export interface TierInfo {
  size: number;
  etag: string;
}

/** A file as seen by the requesting user (already audience-filtered by the server). */
export interface SongFileDTO {
  id: string;
  songId: string;
  kind: FileKind;
  title: string;
  audienceMask: number;
  rev: number;
  sort: number;
  updatedAt: number;
  origName: string | null;
  origMime: string | null;
  origSize: number | null;
  // sheets
  pageCount: number | null;
  pages: PageSize[] | null;
  tiers: Partial<Record<SheetTier, TierInfo>> | null;
  // audio
  durationMs: number | null;
  // lyrics (inline)
  lyricsText: string | null;
}

export interface SongDTO {
  id: string;
  title: string;
  composer: string | null;
  arranger: string | null;
  lyricist: string | null;
  notes: string | null;
  updatedAt: number;
  files: SongFileDTO[];
  collectionIds: string[];
}

/** GET /api/songs — light list (non-editors only get songs with ≥ 1 visible file). */
export interface SongListItemDTO {
  id: string;
  title: string;
  composer: string | null;
  arranger: string | null;
  updatedAt: number;
  counts: { sheet: number; audio: number; lyrics: number };
  collectionIds: string[];
}

export interface CollectionEntryDTO {
  entryId: string;
  songId: string;
  /** 0..n-1 over the entries this viewer gets (hidden or deleted songs leave no gap). */
  position: number;
  note: string | null;
}

export interface CollectionDTO {
  id: string;
  kind: 'folder' | 'setlist';
  name: string;
  description: string | null;
  sort: number;
  updatedAt: number;
  entries: CollectionEntryDTO[];
}

export interface UpsertSongRequest {
  id: string;
  title: string;
  composer?: string | null;
  arranger?: string | null;
  lyricist?: string | null;
  notes?: string | null;
}

/** POST /api/files — create a file record (rev 0) before uploading its parts. */
export interface CreateFileRequest {
  id: string;
  songId: string;
  kind: FileKind;
  title: string;
  audience: AudienceGroup[];
  sort?: number;
  lyricsText?: string | null;
}

/** PATCH /api/files/:id — metadata only (title, audience, sort, lyrics). */
export interface UpdateFileRequest {
  title?: string;
  audience?: AudienceGroup[];
  sort?: number;
  /**
   * Lyrics files only: new text (a change bumps rev); null / '' is rejected (422) for lyrics.
   * On sheet/audio files null / '' is ignored (no change) and any other text is rejected (422).
   */
  lyricsText?: string | null;
}

/**
 * Upload flow for a new revision:
 *   PUT /api/files/:id/rev/:rev/:part   (raw body, Content-Length required; rev = current rev + 1)
 *   POST /api/files/:id/commit { rev, ... } → becomes current; old parts move to r2_trash.
 */
export interface CommitFileRequest {
  rev: number;
  origName?: string | null;
  pageCount?: number;
  pages?: PageSize[];
  durationMs?: number | null;
}

export interface UpsertCollectionRequest {
  id: string;
  kind: 'folder' | 'setlist';
  name: string;
  description?: string | null;
  sort?: number;
}

/**
 * PUT /api/collections/:id/entries — replaces the full ordered list (so it must carry EVERY entry,
 * notes included). `baseUpdatedAt` (optional): the CollectionDTO.updatedAt the list was built from;
 * when the collection has changed since, nothing is written and the answer is 409 with
 * details { reason: COLLECTION_CHANGED }. Without it the last write wins (older clients).
 */
export interface SetCollectionEntriesRequest {
  entries: { entryId: string; songId: string; note?: string | null }[];
  baseUpdatedAt?: number;
}

/** error.details.reason of the 409 above. */
export const COLLECTION_CHANGED = 'collection_changed';

// ───────────── Shared limits (server enforces; client uses them to pre-validate and set maxLength) ─────────────

const MiB = 1024 * 1024;

/** Upload size caps per part (bytes). PUT returns 413 payload_too_large above these. */
export const UPLOAD_MAX_BYTES = {
  pdf: 50 * MiB,
  bundle: 60 * MiB,
  audio: 95 * MiB,
} as const;

/**
 * Library bytes in R2 above which uploads are refused with 413 quota_exceeded: non-deleted files plus
 * deleted files and replaced revisions whose objects still wait in r2_trash (purged after 30 days).
 */
export const STORAGE_QUOTA_BYTES = 9_000_000_000;

/**
 * Audio Content-Types stored for audio originals. PUT …/orig also accepts common aliases
 * (audio/mp3, audio/x-flac, audio/wave, audio/vnd.dlna.adts, audio/opus …) and stores the canonical
 * type from this list; anything else → 415.
 */
export const AUDIO_MIME_ALLOWLIST = [
  'audio/mpeg',
  'audio/mp4',
  'audio/x-m4a',
  'audio/aac',
  'audio/wav',
  'audio/x-wav',
  'audio/ogg',
  'audio/flac',
] as const;
export type AudioMime = (typeof AUDIO_MIME_ALLOWLIST)[number];

/** Parts that must be uploaded (at rev = current + 1) before POST /files/:id/commit. Lyrics have none. */
export const REQUIRED_PARTS: Record<FileKind, readonly MediaPart[]> = {
  sheet: ['orig', 's', 'm', 'l'],
  audio: ['orig'],
  lyrics: [],
};

/** Text/array limits shared by client forms and server validation. */
export const LIBRARY_LIMITS = {
  songTitle: 200,
  songPerson: 200, // composer / arranger / lyricist
  songNotes: 5000,
  fileTitle: 200,
  fileName: 200,
  lyricsText: 50_000,
  collectionName: 120,
  collectionDescription: 2000,
  collectionEntries: 300,
  entryNote: 200,
  maxPages: 500,
} as const;

/** PUT /api/files/:id/rev/:rev/:part → stored object size + R2 etag (unquoted). */
export interface PutPartResponse {
  size: number;
  etag: string;
}
